import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, type LedgerEntry } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, StatTile, type Column, type BadgeTone } from '@/components/ui';
import { km, dateTime } from '@/lib/adminFormat';
import { useAdminPermission } from '@/lib/useAdminPermission';

/** WalletPanel — balance + ledger + a manual-adjustment form for one
 *  (scope,user). Shared by the Wallet page and the entity drawers. The form is
 *  the MAKER step of dual-control: it QUEUES an adjustment for approval (no money
 *  moves). A DIFFERENT super-admin approves it on the Wallet Approvals page before
 *  wallet-svc credits/debits. */
export function WalletPanel({ scope, userId }: { scope: 'customer' | 'driver'; userId: number }) {
  const qc = useQueryClient();
  const queryKey = ['admin', 'wallet', scope, userId];

  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: ({ signal }) => adminApi.wallet(scope, userId, 100, signal),
  });

  const ledger = data?.ledger ?? [];
  const balanceKm = data?.wallet?.balance_km ?? 0;
  const rate = data?.buying_rate_per_km ?? 0;
  // Only finance + superadmin may post adjustments; ops/support see the wallet
  // read-only. (admin-svc enforces this too — this just hides the control.)
  const canAdjust = useAdminPermission('wallet.adjust');

  const columns: Array<Column<LedgerEntry>> = [
    { key: 'type', header: 'Type', cell: (r) => <LedgerTypeBadge type={r.type} /> },
    {
      key: 'amount',
      header: 'Δ km',
      align: 'right',
      cell: (r) => (
        <span className={`metric text-sm ${r.amount_km < 0 ? 'text-danger' : 'text-ok'}`}>
          {r.amount_km > 0 ? '+' : ''}
          {r.amount_km.toFixed(2)}
        </span>
      ),
    },
    { key: 'dollars', header: '$', align: 'right', cell: (r) => <span className="metric text-xs text-muted">{r.dollars ? `$${r.dollars.toFixed(2)}` : '—'}</span> },
    { key: 'ref', header: 'Ref', cell: (r) => <span className="text-xs text-slate-300 truncate" title={r.ref}>{r.ref || '—'}</span> },
    { key: 'when', header: 'When', cell: (r) => <span className="text-xs text-muted">{dateTime(r.created_at)}</span> },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <StatTile
          label={scope === 'customer' ? 'km-credit balance' : 'Earnings balance'}
          value={scope === 'customer' ? km(balanceKm) : `$${balanceKm.toFixed(2)}`}
          tone={balanceKm < 0 ? 'danger' : 'neutral'}
          hint={data?.wallet?.exists ? `updated ${dateTime(data.wallet.updated_at)}` : 'no wallet row yet'}
        />
        <StatTile label="Buying rate" value={`$${rate.toFixed(2)}/km`} hint="dollars → km conversion" />
      </div>

      {canAdjust ? (
        <AdjustForm
          scope={scope}
          userId={userId}
          rate={rate}
          onDone={() => qc.invalidateQueries({ queryKey })}
        />
      ) : (
        <Card>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Manual adjustment</div>
          <p className="text-xs text-muted">
            Read-only — your role can view this wallet but not post adjustments. (Wallet
            adjustments require the <code className="text-slate-400">finance</code> role.)
          </p>
        </Card>
      )}

      <Card flush>
        <div className="border-b border-ink-800 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted">
          Ledger
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Spinner label="Loading ledger…" />
          </div>
        ) : isError ? (
          <EmptyState title="Couldn't load ledger" description={(error as Error)?.message} />
        ) : (
          <DataTable<LedgerEntry>
            columns={columns}
            rows={ledger}
            rowKey={(r) => String(r.id)}
            stickyHeader
            empty={<EmptyState title="No ledger entries" description="This wallet has no recorded mutations yet." />}
          />
        )}
      </Card>
    </div>
  );
}

/** The MAKER form. Captures a dollar amount + direction (credit/debit) + an
 *  audit note, and QUEUES the adjustment for approval — no money moves here. A
 *  live km preview is shown for customer (km-credit) wallets. */
function AdjustForm({
  scope,
  userId,
  rate,
  onDone,
}: {
  scope: 'customer' | 'driver';
  userId: number;
  rate: number;
  onDone: () => void;
}) {
  const [direction, setDirection] = useState<'credit' | 'debit'>('credit');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [ok, setOk] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => adminApi.adjustWallet(scope, userId, { direction, dollars: Number(amount), note }),
    onSuccess: (res) => {
      setOk(`Queued for approval (#${res.id}). A different super-admin must approve it on Wallet Approvals before it applies.`);
      setAmount('');
      setNote('');
      onDone();
    },
  });

  const n = Number(amount);
  const validNumber = amount !== '' && Number.isFinite(n) && n > 0;
  const kmPreview = scope === 'customer' && rate > 0 && validNumber ? (n / rate).toFixed(2) : null;

  return (
    <Card>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Manual adjustment</div>
      <p className="mb-3 text-xs text-muted">
        <strong className="text-slate-300">Dual-control:</strong> this queues a{' '}
        <code className="text-slate-400">{direction}</code> for approval — no money moves until a{' '}
        <em>different</em> super-admin approves it on Wallet Approvals.
        {scope === 'driver' ? ' Amount is in AUD.' : ' Amount is in dollars (converted to km-credit).'}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="block text-[11px] text-text-secondary">Direction</label>
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value as 'credit' | 'debit')}
            className="input mt-1 w-auto"
          >
            <option value="credit">credit (+)</option>
            <option value="debit">debit (−)</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-text-secondary">Amount ($)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 50"
            className="input mt-1 w-32"
          />
        </div>
        <div className="min-w-0 flex-1">
          <label className="block text-[11px] text-text-secondary">Note (audit)</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="reason for the adjustment"
            className="input mt-1"
          />
        </div>
        <button
          type="button"
          disabled={!validNumber || mutation.isPending}
          onClick={() => {
            setOk(null);
            mutation.mutate();
          }}
          className="btn-primary"
        >
          {mutation.isPending ? 'Queuing…' : 'Queue for approval'}
        </button>
      </div>
      {kmPreview ? <p className="mt-2 text-xs text-muted">≈ {kmPreview} km at ${rate.toFixed(2)}/km</p> : null}
      {ok ? <p className="mt-2 text-xs text-ok">{ok}</p> : null}
      {mutation.isError ? <p className="mt-2 text-xs text-danger">{(mutation.error as Error)?.message}</p> : null}
    </Card>
  );
}

/** Ledger entry type → a toned badge. */
function LedgerTypeBadge({ type }: { type: string }) {
  const tone: BadgeTone =
    type === 'topup' || type === 'adjustment' ? 'info' : type === 'ride' || type === 'toll' ? 'warn' : type === 'payout' ? 'danger' : 'neutral';
  return <Badge tone={tone}>{type}</Badge>;
}
